import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getTitleFromMap, buildRouteTitleMap } from './routeTitleMap'

interface TitleContextType {
  setTitle: (title: string) => void
  title: string
  setDynamicTitle: (dynamicPart: string | null) => void
}

/**
 * For dynamic titles (like detail pages with IDs only returned by the API), we can use
 * setDynamicTitle() to prepend dynamic content to the base title from the route map.
 *
 * Example (on request detail page component):
 *   const { setDynamicTitle } = useTitle()
 *   setDynamicTitle('REQ-123') // Results in "REQ-123 | Request Detail | Omnea"
 */

const PageTitleContext = createContext<TitleContextType | undefined>(undefined)
const fallbackTitle = 'Omnea'

export const PageTitleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('route-titles')
  const routeTitleMap = useMemo(() => buildRouteTitleMap(t), [t])

  const [title, setTitle] = useState(fallbackTitle)
  const [dynamicTitle, setDynamicTitle] = useState<string | null>(null)
  const router = useRouter()

  const getBaseTitle = useCallback(() => {
    const baseTitle = getTitleFromMap(router.asPath, routeTitleMap)
    return baseTitle || fallbackTitle
  }, [router.asPath, routeTitleMap])

  useEffect(() => {
    // Clear dynamic part of title on route change
    if (dynamicTitle) setDynamicTitle(null)
    // dynamicTitle is excluded from the dependency array to prevent an infinite render loop
  }, [router.asPath]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const devPrefix = process.env.NODE_ENV === 'development' ? '[DEV] ' : ''
    const baseTitle = getBaseTitle()

    if (dynamicTitle) {
      setTitle(`${devPrefix}${dynamicTitle} | ${baseTitle}`)
    } else {
      setTitle(`${devPrefix}${baseTitle}`)
    }
  }, [dynamicTitle, router.asPath, getBaseTitle])

  return (
    <PageTitleContext.Provider value={{ title, setTitle, setDynamicTitle }}>
      <Head>
        <title>{title}</title>
      </Head>
      {children}
    </PageTitleContext.Provider>
  )
}

export const useTitle = () => {
  const context = useContext(PageTitleContext)
  if (!context) {
    throw new Error('useTitle must be used within a TitleProvider')
  }
  return context
}
